import { CRM_TYPES } from "../constants/crmConstants";

/**
 * Output format constants for formatOpportunityId.
 * DEFAULT → "EAI 1234"   (space-separated, standard)
 * HASH_SUFFIX → "EAI#1234"
 * HASH_PREFIX → "#EAI1234"
 */
export const OPPORTUNITY_ID_FORMAT = Object.freeze({
  DEFAULT: "default",
  HASH_SUFFIX: "hash-suffix",
  HASH_PREFIX: "hash-prefix",
});

/**
 * When a CRM opportunity id is longer than OPPORTUNITY_ID_MAX_LENGTH characters, the
 * display label is truncated to its last OPPORTUNITY_ID_VISIBLE_LENGTH characters
 * (the full id is still shown on hover). Tune here.
 */
export const OPPORTUNITY_ID_MAX_LENGTH = 12;
export const OPPORTUNITY_ID_VISIBLE_LENGTH = 7;

/**
 * Format an opportunity ID with configurable output format.
 *
 * Normalizes any pre-existing format (strips leading/trailing "#", embedded CRM prefix)
 * before applying the requested output format.
 *
 *   formatOpportunityId("EAI", 1234)                              → "EAI 1234"
 *   formatOpportunityId("EAI#", "1234")                           → "EAI 1234"
 *   formatOpportunityId("EAI", "#1234")                           → "EAI 1234"
 *   formatOpportunityId("EAI", "EAI #1234")                       → "EAI 1234"
 *   formatOpportunityId("EAI", 1234, OPPORTUNITY_ID_FORMAT.HASH_SUFFIX) → "EAI#1234"
 *   formatOpportunityId("EAI", 1234, OPPORTUNITY_ID_FORMAT.HASH_PREFIX) → "#EAI1234"
 *
 * @param {string} crmName - The CRM display name (e.g. "EAI", "DealerSocket", "EAI#")
 * @param {string|number} id - The raw or pre-formatted opportunity ID
 * @param {string} [format=OPPORTUNITY_ID_FORMAT.DEFAULT] - One of OPPORTUNITY_ID_FORMAT values
 * @returns {string}
 */
export const formatOpportunityId = (crmName, id, format = OPPORTUNITY_ID_FORMAT.DEFAULT) => {
  const name = String(crmName || "EAI").trim();
  if (id === null || id === undefined || id === "") return name;
  const rawId = String(id).trim();

  switch (format) {
    case OPPORTUNITY_ID_FORMAT.HASH_SUFFIX:
      return `${name}#${rawId}`;
    case OPPORTUNITY_ID_FORMAT.HASH_PREFIX:
      return `#${name}${rawId}`;
    default:
      return `${name} ${rawId}`;
  }
};

/**
 * Sentinel id used by the "All" option in the opportunity-select dropdown.
 * When this is the active selection, outgoing messages/MMS are not tied to a
 * specific opportunity, so `leadOpportunityId` must be sent as null.
 */
export const ALL_OPPORTUNITIES_ID = "#DEFAULTOPPID";

/**
 * Sentinel id used by the top-level "All opportunity" option in the
 * opportunity-select dropdown. Unlike ALL_OPPORTUNITIES_ID (which still
 * scopes to the active root opp_type tab), this clears ALL opportunity
 * scoping — both leadOpportunityId and selectedOppTypeId are sent as null.
 */
export const ALL_OPPORTUNITY_TYPES_ID = "#ALL_OPPORTUNITY";

/**
 * Resolve the `leadOpportunityId` value to send with CREATE_MESSAGE / CREATE_MMS
 * (and similar) mutations based on the currently selected opportunity.
 *
 * Returns null when "All" is selected (sentinel ALL_OPPORTUNITIES_ID), otherwise
 * the selected opportunity id coerced to a number.
 *
 * @param {string|number} messageOpportunity - Selected opportunity id from redux
 * @returns {number|null}
 */
export const resolveLeadOpportunityId = (messageOpportunity, selectedOppTypeId = null) => {
  if (messageOpportunity === ALL_OPPORTUNITY_TYPES_ID) return null;
  return messageOpportunity === ALL_OPPORTUNITIES_ID ? selectedOppTypeId : +messageOpportunity;
};

/**
 * Maps CRM types to functions that extract opportunity IDs from opportunity objects
 */
export const CRM_OPPORTUNITY_EXTRACTORS = Object.freeze({
  [CRM_TYPES.DEALERSOCKET]: opp => opp?.dsOpportunityEvents?.[0]?.dsEventId,
  [CRM_TYPES.DEALER_TEAM]: opp => opp?.dtLeadOpportunities?.[0]?.dtOpportunityId,
  [CRM_TYPES.SALESFORCE]: opp => opp?.sfLeadOpportunities?.[0]?.sfOpportunityId,
  [CRM_TYPES.VIN_SOLUTIONS]: opp => opp?.vsLeadOpportunities?.[0]?.vsLeadId,
  [CRM_TYPES.EL]: opp => opp?.elLeadOpportunities?.[0]?.elOpportunityId,
  [CRM_TYPES.MOMENTUM]: opp => opp?.mfLeadOpportunities?.[0]?.mfLeadId,
  // Tekion: priority order tk_lead_id → tk_repair_order_id →
  // tk_appointment_id → tk_customer_id. Service-appointment and
  // repair-order opps are synthesized on our side without a real
  // CRM lead id; without this fallthrough the card would render
  // "EAI <internal-id>" even when there's a perfectly good RO /
  // appointment mapping (and a working BE-resolved crm_url).
  [CRM_TYPES.TEKION]: opp => {
    const tk = opp?.tkLeadOpportunities?.[0];
    if (!tk) return undefined;
    return tk.tkLeadId || tk.tkRepairOrderId || tk.tkAppointmentId || tk.tkCustomerId;
  },
});

/**
 * Maps CRM types to functions that extract lead IDs from CRM lead objects
 */
export const CRM_LEAD_ID_EXTRACTORS = Object.freeze({
  [CRM_TYPES.DEALERSOCKET]: lead => lead?.dsExtractedLead?.dsLeadId,
  [CRM_TYPES.DEALER_TEAM]: lead => lead?.dtExtractedLead?.dtLeadId,
  [CRM_TYPES.SALESFORCE]: lead => lead?.sfExtractedLead?.sfLeadId,
  [CRM_TYPES.MOMENTUM]: lead =>
    lead?.mfExtractedLead?.mfProspectId || lead?.mfExtractedLead?.mfCustomerId,
  [CRM_TYPES.TEKION]: lead => lead?.tkExtractedLead?.tkLeadId,
});

/**
 * Get formatted opportunity display name
 * @param {Object} opportunity - The opportunity object
 * @returns {string} Formatted display name
 */
export const getOpportunityDisplayName = opportunity => {
  if (!opportunity) return "";

  const { name, prefix, eventId, opportunityStatus } = opportunity;

  const formattedEventId = String(eventId).padStart(3, "0");
  const parentStatusName = opportunityStatus?.parentStatus?.name || "";
  const statusName = opportunityStatus?.name || "Unknown";

  return `${name} (${formatOpportunityId(
    prefix,
    formattedEventId
  )} / ${parentStatusName} - ${statusName})`;
};

/**
 * Extract lead ID based on CRM integration type
 * @param {Object} activeLeadOppr - The active lead opportunity object
 * @param {string} crmIntegrationType - CRM integration type
 * @returns {string|null} Lead ID
 */
export const extractLeadId = (activeLeadOppr, crmIntegrationType) => {
  if (!activeLeadOppr || !crmIntegrationType) return null;

  switch (crmIntegrationType) {
    case CRM_TYPES.DEALERSOCKET:
      return activeLeadOppr?.dsExtractedLead?.dsLeadId || null;
    case CRM_TYPES.DEALER_TEAM:
      return activeLeadOppr?.dtExtractedLead?.dtLeadId || null;
    case CRM_TYPES.SALESFORCE:
      return activeLeadOppr?.sfExtractedLead?.sfLeadId || null;
    case CRM_TYPES.MOMENTUM:
      return (
        activeLeadOppr?.mfExtractedLead?.mfProspectId ||
        activeLeadOppr?.mfExtractedLead?.mfCustomerId ||
        null
      );
    case CRM_TYPES.EL:
      return activeLeadOppr?.elExtractedLead?.elLeadId || null;
    case CRM_TYPES.VIN_SOLUTIONS:
      return activeLeadOppr?.vsExtractedLead?.vsLeadId || null;
    case CRM_TYPES.TEKION:
      return activeLeadOppr?.tkExtractedLead?.tkLeadId || null;
    default:
      return null;
  }
};

/**
 * Extract opportunity ID based on CRM integration type
 * @param {Object} item - The opportunity item object
 * @param {string} crmIntegrationType - CRM integration type
 * @param {string} dsLeadId - Optional DS lead ID to match against dsOpportunityEvents
 * @returns {string} Opportunity ID
 */
export const extractOpportunityId = (item, crmIntegrationType, dsLeadId) => {
  if (!item || !crmIntegrationType) return item?.id;

  switch (crmIntegrationType) {
    case CRM_TYPES.DEALERSOCKET: {
      const events = item?.dsOpportunityEvents;
      if (!events?.length) return item?.id;
      const matched = dsLeadId ? events.find(e => String(e.dsLeadId) === String(dsLeadId)) : null;
      return (matched || events[0])?.dsEventId || item?.id;
    }
    case CRM_TYPES.DEALER_TEAM:
      return item?.dtLeadOpportunities?.length > 0
        ? item.dtLeadOpportunities[0].dtOpportunityId
        : item?.id;
    case CRM_TYPES.SALESFORCE:
      return item?.sfLeadOpportunities?.length > 0
        ? item.sfLeadOpportunities[0].sfOpportunityId
        : item?.id;
    case CRM_TYPES.VIN_SOLUTIONS:
      return item?.vsLeadOpportunities?.length > 0
        ? item.vsLeadOpportunities[0].vsLeadId
        : item?.id;
    case CRM_TYPES.EL:
      return item?.elLeadOpportunities?.length > 0
        ? item.elLeadOpportunities[0].elOpportunityId
        : item?.id;
    case CRM_TYPES.MOMENTUM:
      return item?.mfLeadOpportunities?.length > 0
        ? item.mfLeadOpportunities[0].mfLeadId
        : item?.id;
    case CRM_TYPES.TEKION:
      return item?.tkLeadOpportunities?.length > 0
        ? item.tkLeadOpportunities[0].tkLeadId
        : item?.id;
    default:
      return item?.id;
  }
};

/**
 * Extract link opportunity ID based on CRM integration type
 * Used for generating external CRM links
 * @param {Object} item - The opportunity item object
 * @param {string} crmIntegrationType - CRM integration type
 * @param {Object} selectedLead - The selected lead object (for EL integration)
 * @param {string} dsLeadId - Optional DS lead ID to match against dsOpportunityEvents
 * @returns {string} Link opportunity ID
 */
/* eslint-disable default-param-last */
export const extractLinkOpportunityId = (
  item,
  crmIntegrationType,
  selectedLead = null,
  dsLeadId
) => {
  if (!item || !crmIntegrationType) return item?.id;

  switch (crmIntegrationType) {
    case CRM_TYPES.VIN_SOLUTIONS:
      return item?.vsLeadOpportunities?.length > 0
        ? item.vsLeadOpportunities[0].vsContactId
        : extractOpportunityId(item, crmIntegrationType);
    case CRM_TYPES.EL:
      return selectedLead?.fullName || "";
    default:
      return extractOpportunityId(item, crmIntegrationType, dsLeadId);
  }
};
/* eslint-enable default-param-last */

/**
 * Get CRM label (short display code) based on integration type.
 *
 * Returns 2-3 letter short codes (DS / DT / SF / EL / TK / VIN / MF)
 * instead of full product names so the opp-card title (e.g.
 * "DS 6a3938523e343b1ea") fits in narrow layouts like the browser
 * extension without the CRM name itself getting truncated to
 * "DealerS...". The full CRM name is still surfaced via the
 * tooltip-fullLabel on hover and via the lead-opp card's
 * "DealerSocket Opportunity ID" caption below the title.
 *
 * @param {string} crmIntegrationType - CRM integration type
 * @param {boolean} hasCrmIntegration - Whether the opportunity has CRM integration
 * @returns {string} CRM short code without any punctuation
 */
const getCrmLabel = (crmIntegrationType, hasCrmIntegration) => {
  if (!hasCrmIntegration) return "EAI";

  switch (crmIntegrationType) {
    case CRM_TYPES.DEALERSOCKET:
      return "DS";
    case CRM_TYPES.DEALER_TEAM:
      return "DT";
    case CRM_TYPES.SALESFORCE:
      return "SF";
    case CRM_TYPES.MOMENTUM:
      return "MF";
    case CRM_TYPES.EL:
      return "EL";
    case CRM_TYPES.VIN_SOLUTIONS:
      return "VIN";
    case CRM_TYPES.TEKION:
      return "TK";
    default:
      return "EAI";
  }
};

/**
 * Get default opportunity from a list based on priority:
 * 1. If only one opportunity, return it
 * 2. If multiple, find active ones and return the most recently updated
 * 3. If no active ones, return the most recently updated from all
 * @param {Array} opportunities - Array of opportunity objects with isActive and updatedDate
 * @returns {Object|null} The default opportunity or null
 */
export const getDefaultOpportunity = opportunities => {
  if (!opportunities || opportunities?.length === 0) return null;

  if (opportunities.length === 1) {
    return opportunities[0];
  }

  const getTime = date => (date ? new Date(date).getTime() : 0);
  const getMostRecent = list =>
    list.reduce((latest, opp) =>
      getTime(opp.updatedDate) > getTime(latest.updatedDate) ? opp : latest
    );

  const activeOpportunities = opportunities.filter(opp => opp.isActive);
  if (activeOpportunities.length > 0) return getMostRecent(activeOpportunities);

  return getMostRecent(opportunities);
};

/**
 * Prepare opportunity list with consistent formatting.
 *
 * The CRM name/id is decided PER OPPORTUNITY: an opportunity is labeled with the CRM
 * name only when it has its own real CRM id (exists AND differs from the internal id —
 * a CRM sub-array can carry a null id field, e.g. Tekion service-appointment opps).
 * Otherwise it falls back to "EAI <internal id>". The lead-level CRM id is intentionally
 * NOT used here: a lead with one real CRM opportunity and two EAI-only opportunities
 * must show 1 CRM-labeled + 2 EAI, not all three CRM-labeled.
 *
 * @param {Array} leadOpportunities - Array of lead opportunities
 * @param {string} crmIntegrationType - CRM integration type
 * @returns {Array} Formatted opportunity list ({ id, name, opportunityStatus, eventId, prefix, isActive, updatedDate })
 */
export const prepareOpportunityList = (leadOpportunities, crmIntegrationType) => {
  if (!leadOpportunities || !Array.isArray(leadOpportunities)) return [];

  return leadOpportunities.filter(Boolean).map(item => {
    const opprCrmId = extractOpportunityId(item, crmIntegrationType);
    const hasCrmId = Boolean(opprCrmId) && String(opprCrmId) !== String(item.id);

    return {
      id: String(item.id),
      name: item.name,
      opportunityStatus: item.opportunityStatus,
      eventId: String(hasCrmId ? opprCrmId : item.id),
      prefix: getCrmLabel(crmIntegrationType, hasCrmId),
      isActive: item?.isActive,
      updatedDate: item?.updatedDate,
    };
  });
};
